import { useEffect, useState, useRef } from "react";
import axios from "axios";
import axiosConfig from "../../config/axios";
import photoImg from "../../assets/icons/photo.svg";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import Select from "react-select";

import {
  Editor,
  EditorState,
  RichUtils,
  Modifier,
  CompositeDecorator,
  SelectionState,
  ContentState,
} from "draft-js";
import { stateFromHTML } from "draft-js-import-html";
import { stateToHTML } from "draft-js-export-html";
import "draft-js/dist/Draft.css";
import boldImg from "../../assets/icons/bold52.png";
import italicImg from "../../assets/icons/italic52.png";
import underlineImg from "../../assets/icons/underline52.png";
import linkImg from "../../assets/icons/link50.png";
import ImprovisedButton from "../AdminPage/ImrpovisedButton/ImproButton";

import success from "../../assets/gif/success.gif";
import failed from "../../assets/gif/failed.webp";

import { logout } from "../../features/userSlice";
import { useFetchCategories } from "../../hooks/useCategory";
import {
  useCreatePredefinedRoute,
  useFetchPredefinedRouteById,
  useUpdatePredefinedRoute,
  useSearchLocationsForWaypoints,
} from "../../hooks/usePredefinedRoutes";

import "./PredefinedRouteForm.css";
import AdminNavigationLinks from "../../Components/AdminNavigationLinks/AdminNavigationLinks";

const findLinkEntities = (contentBlock, callback, contentState) => {
  contentBlock.findEntityRanges((character) => {
    const entityKey = character.getEntity();
    return (
      entityKey !== null &&
      contentState.getEntity(entityKey).getType() === "LINK"
    );
  }, callback);
};

const Hyperlink = (props) => {
  const { url } = props.contentState.getEntity(props.entityKey).getData();
  return (
    <a
      href={url}
      onClick={(e) => {
        e.preventDefault();
        window.open(url, "_blank");
      }}
      style={{ color: "#3b5998", textDecoration: "underline" }}
    >
      {props.children}
    </a>
  );
};

const createLinkDecorator = () =>
  new CompositeDecorator([
    {
      strategy: findLinkEntities,
      component: Hyperlink,
    },
  ]);

function convertFromHTML(htmlString) {
  try {
    const contentState = stateFromHTML(htmlString || "");
    return EditorState.createWithContent(contentState, createLinkDecorator());
  } catch {
    return EditorState.createEmpty(createLinkDecorator());
  }
}

function convertToHTMLWrapper(editorState) {
  return stateToHTML(editorState.getCurrentContent());
}

const getImagePreviewSrc = (value) => {
  if (!value) return "";

  if (value.startsWith("http://") || value.startsWith("https://")) {
    return value;
  }

  return `https://api.admin.meetserbia.rs/images/${encodeURIComponent(value)}`;
};

const MAX_SIZE_MB = 20;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

const PredefinedRouteForm = () => {
  const { id } = useParams();
  const isEditMode = !!id;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: categories = [], isLoading: isLoadingCategories } =
    useFetchCategories();

  const { predefinedRoute, isLoading: isLoadingRoute } =
    useFetchPredefinedRouteById(id);

  const { mutateAsync: createRoute } = useCreatePredefinedRoute();
  const { mutateAsync: updateRoute } = useUpdatePredefinedRoute();

  const [titleEnglish, setTitleEnglish] = useState("");
  const [titleLatin, setTitleLatin] = useState("");
  const [titleCyrillic, setTitleCyrillic] = useState("");

  const [location, setLocation] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [descriptionEnglish, setDescriptionEnglish] = useState(() =>
    EditorState.createEmpty(createLinkDecorator()),
  );
  const [descriptionLatin, setDescriptionLatin] = useState(() =>
    EditorState.createEmpty(createLinkDecorator()),
  );
  const [descriptionCyrillic, setDescriptionCyrillic] = useState(() =>
    EditorState.createEmpty(createLinkDecorator()),
  );

  const [duration, setDuration] = useState("");
  const [highlightsEnglish, setHighlightsEnglish] = useState(() =>
    EditorState.createEmpty(createLinkDecorator()),
  );
  const [highlightsLatin, setHighlightsLatin] = useState(() =>
    EditorState.createEmpty(createLinkDecorator()),
  );
  const [highlightsCyrillic, setHighlightsCyrillic] = useState(() =>
    EditorState.createEmpty(createLinkDecorator()),
  );

  const [recommendedEnglish, setRecommendedEnglish] = useState(() =>
    EditorState.createEmpty(createLinkDecorator()),
  );
  const [recommendedLatin, setRecommendedLatin] = useState(() =>
    EditorState.createEmpty(createLinkDecorator()),
  );
  const [recommendedCyrillic, setRecommendedCyrillic] = useState(() =>
    EditorState.createEmpty(createLinkDecorator()),
  );

  const [isFavorite, setIsFavorite] = useState(false);
  const [priority, setPriority] = useState(0);
  const [waypoints, setWaypoints] = useState([]);

  const [isUploading, setIsUploading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [waypointSearch, setWaypointSearch] = useState("");
  const { locationSuggestions, isLoading: isLoadingWaypointSuggestions } =
    useSearchLocationsForWaypoints(waypointSearch);

  const photoInputRef = useRef(null);

  const editorDescriptionEngRef = useRef(null);
  const editorDescriptionLatRef = useRef(null);
  const editorDescriptionCirRef = useRef(null);

  const editorHighlightsEngRef = useRef(null);
  const editorHighlightsLatRef = useRef(null);
  const editorHighlightsCirRef = useRef(null);

  const editorRecommendedEngRef = useRef(null);
  const editorRecommendedLatRef = useRef(null);
  const editorRecommendedCirRef = useRef(null);

  const [activeStylesDescriptionEng, setActiveStylesDescriptionEng] = useState({
    BOLD: false,
    ITALIC: false,
    UNDERLINE: false,
  });
  const [activeStylesDescriptionLat, setActiveStylesDescriptionLat] = useState({
    BOLD: false,
    ITALIC: false,
    UNDERLINE: false,
  });
  const [activeStylesDescriptionCir, setActiveStylesDescriptionCir] = useState({
    BOLD: false,
    ITALIC: false,
    UNDERLINE: false,
  });

  const [activeStylesHighlightsEng, setActiveStylesHighlightsEng] = useState({
    BOLD: false,
    ITALIC: false,
    UNDERLINE: false,
  });
  const [activeStylesHighlightsLat, setActiveStylesHighlightsLat] = useState({
    BOLD: false,
    ITALIC: false,
    UNDERLINE: false,
  });
  const [activeStylesHighlightsCir, setActiveStylesHighlightsCir] = useState({
    BOLD: false,
    ITALIC: false,
    UNDERLINE: false,
  });

  const [activeStylesRecommendedEng, setActiveStylesRecommendedEng] = useState({
    BOLD: false,
    ITALIC: false,
    UNDERLINE: false,
  });
  const [activeStylesRecommendedLat, setActiveStylesRecommendedLat] = useState({
    BOLD: false,
    ITALIC: false,
    UNDERLINE: false,
  });
  const [activeStylesRecommendedCir, setActiveStylesRecommendedCir] = useState({
    BOLD: false,
    ITALIC: false,
    UNDERLINE: false,
  });

  useEffect(() => {
    if (!predefinedRoute || !categories.length) return;

    setTitleEnglish(predefinedRoute.titleEng || "");
    setTitleLatin(predefinedRoute.titleLat || "");
    setTitleCyrillic(predefinedRoute.titleCir || "");

    setLocation(predefinedRoute.location || "");
    setImageUrl(predefinedRoute.imageUrl || "");
    setImagePreview(getImagePreviewSrc(predefinedRoute.imageUrl || ""));

    const matchedCategory =
      categories.find(
        (category) => category.nameEnglish === predefinedRoute.category,
      ) || null;

    setSelectedCategory(matchedCategory);

    setDescriptionEnglish(convertFromHTML(predefinedRoute.descriptionEng));
    setDescriptionLatin(convertFromHTML(predefinedRoute.descriptionLat));
    setDescriptionCyrillic(convertFromHTML(predefinedRoute.descriptionCir));

    setDuration(predefinedRoute.duration || "");
    setHighlightsEnglish(convertFromHTML(predefinedRoute.highlightsEng));
    setHighlightsLatin(convertFromHTML(predefinedRoute.highlightsLat));
    setHighlightsCyrillic(convertFromHTML(predefinedRoute.highlightsCir));

    setRecommendedEnglish(convertFromHTML(predefinedRoute.recommendedEng));
    setRecommendedLatin(convertFromHTML(predefinedRoute.recommendedLat));
    setRecommendedCyrillic(convertFromHTML(predefinedRoute.recommendedCir));

    setIsFavorite(!!predefinedRoute.isFavorite);
    setPriority(predefinedRoute.priority ?? 0);
    setWaypoints(predefinedRoute.waypoints || []);
  }, [predefinedRoute, categories]);

  const normalizeWaypointOrder = (items) => {
    return items.map((item, index) => ({
      ...item,
      order: index,
    }));
  };

  const resetStatuses = () => {
    setIsSuccess(false);
    setIsFailed(false);
    setErrorMessage("");
  };

  const handleFileUpload = async (file) => {
    const formData = new FormData();

    formData.append("file", file);
    formData.append("fileName", file.name);

    try {
      const res = await axiosConfig.post("/images/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return res.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        switch (error.response?.status) {
          case 400:
            throw new Error("Bad Request: Please check your input.");
          case 401:
            throw new Error("Unauthorized: Please login.");
          case 404:
            throw new Error("Data not found.");
          case 413:
            throw new Error("Entity Too Large");
          case 500:
            throw new Error("Internal server error, please try again later.");
          default:
            throw new Error("An unexpected error occurred.");
        }
      } else {
        throw new Error("Network error, please check your connection.");
      }
    }
  };

  const handleImagePickerClick = () => {
    photoInputRef.current?.click();
  };

  const handleImageUploaded = async (e) => {
    const file = e.target.files?.[0];
    console.log("FILE:", file);
    if (!file) return;

    resetStatuses();

    if (file.size > MAX_SIZE_BYTES) {
      setErrorMessage("Image exceeds 20mb");
      setIsFailed(true);
      return;
    }

    const reader = new FileReader();

    reader.onload = async (event) => {
      try {
        setImagePreview(event.target.result);
        console.log("Uploading file...");

        const uploadedFile = await handleFileUpload(file);
        console.log("UPLOAD RESPONSE:", uploadedFile);
        setImageUrl(uploadedFile.fileId || "");
      } catch (error) {
        setErrorMessage(error.message || "Image upload failed.");
        setIsFailed(true);
        setImagePreview("");
        setImageUrl("");
      }
    };

    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImageUrl("");
    setImagePreview("");

    if (photoInputRef.current) {
      photoInputRef.current.value = "";
    }
  };

  function handlePastedText(text, html, editorState, onChange) {
    const currentContent = editorState.getCurrentContent();
    const selection = editorState.getSelection();

    if (html) {
      const blocksFromHtml = stateFromHTML(html);

      const newContentState = Modifier.replaceWithFragment(
        currentContent,
        selection,
        blocksFromHtml.getBlockMap(),
      );

      const newState = EditorState.push(
        editorState,
        newContentState,
        "insert-fragment",
      );
      onChange(newState);
      return "handled";
    } else if (text) {
      const blocksFromText = ContentState.createFromText(text).getBlockMap();
      const newContentState = Modifier.replaceWithFragment(
        currentContent,
        selection,
        blocksFromText,
      );
      const newState = EditorState.push(
        editorState,
        newContentState,
        "insert-fragment",
      );
      onChange(newState);
      return "handled";
    }

    return "not-handled";
  }

  const promptForLink = (editorState, setEditorState) => {
    const url = window.prompt("Unesite URL za link:");
    if (!url) return;

    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      "LINK",
      "MUTABLE",
      { url },
    );
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const selection = editorState.getSelection();

    const contentStateWithLink = Modifier.applyEntity(
      contentStateWithEntity,
      selection,
      entityKey,
    );

    const linkedEditorState = EditorState.push(
      editorState,
      contentStateWithLink,
      "apply-entity",
    );

    setEditorState(EditorState.forceSelection(linkedEditorState, selection));
  };

  const handleEditorContainerClick = (
    editorRef,
    editorState,
    setEditorState,
  ) => {
    if (editorRef.current) {
      editorRef.current.focus();
      const selection = editorState.getSelection();

      if (!selection.getHasFocus()) {
        const currentContent = editorState.getCurrentContent();
        const lastBlock = currentContent.getBlockMap().last();
        const key = lastBlock.getKey();
        const length = lastBlock.getLength();

        const newSelection = SelectionState.createEmpty(key).merge({
          anchorOffset: length,
          focusOffset: length,
        });

        setEditorState(EditorState.forceSelection(editorState, newSelection));
      }
    }
  };

  const toggleStyle = (style, editor) => {
    let editorState, setEditorState, setActiveStyles, editorRef;

    if (editor === "descriptionEng") {
      editorState = descriptionEnglish;
      setEditorState = setDescriptionEnglish;
      setActiveStyles = setActiveStylesDescriptionEng;
      editorRef = editorDescriptionEngRef;
    } else if (editor === "descriptionLat") {
      editorState = descriptionLatin;
      setEditorState = setDescriptionLatin;
      setActiveStyles = setActiveStylesDescriptionLat;
      editorRef = editorDescriptionLatRef;
    } else if (editor === "descriptionCir") {
      editorState = descriptionCyrillic;
      setEditorState = setDescriptionCyrillic;
      setActiveStyles = setActiveStylesDescriptionCir;
      editorRef = editorDescriptionCirRef;
    } else if (editor === "highlightsEng") {
      editorState = highlightsEnglish;
      setEditorState = setHighlightsEnglish;
      setActiveStyles = setActiveStylesHighlightsEng;
      editorRef = editorHighlightsEngRef;
    } else if (editor === "highlightsLat") {
      editorState = highlightsLatin;
      setEditorState = setHighlightsLatin;
      setActiveStyles = setActiveStylesHighlightsLat;
      editorRef = editorHighlightsLatRef;
    } else if (editor === "highlightsCir") {
      editorState = highlightsCyrillic;
      setEditorState = setHighlightsCyrillic;
      setActiveStyles = setActiveStylesHighlightsCir;
      editorRef = editorHighlightsCirRef;
    } else if (editor === "recommendedEng") {
      editorState = recommendedEnglish;
      setEditorState = setRecommendedEnglish;
      setActiveStyles = setActiveStylesRecommendedEng;
      editorRef = editorRecommendedEngRef;
    } else if (editor === "recommendedLat") {
      editorState = recommendedLatin;
      setEditorState = setRecommendedLatin;
      setActiveStyles = setActiveStylesRecommendedLat;
      editorRef = editorRecommendedLatRef;
    } else if (editor === "recommendedCir") {
      editorState = recommendedCyrillic;
      setEditorState = setRecommendedCyrillic;
      setActiveStyles = setActiveStylesRecommendedCir;
      editorRef = editorRecommendedCirRef;
    }

    if (document.activeElement !== editorRef.current) {
      editorRef.current.focus();
    }

    const newEditorState = RichUtils.toggleInlineStyle(editorState, style);
    setEditorState(newEditorState);

    setActiveStyles((prevStyles) => ({
      ...prevStyles,
      [style]: !prevStyles[style],
    }));
  };

  const applyStyles = (
    editorState,
    setEditorState,
    setActiveStyles,
    activeStyles,
  ) => {
    const currentStyle = editorState.getCurrentInlineStyle();
    const selection = editorState.getSelection();

    if (!selection.isCollapsed()) {
      const updatedActiveStyles = {};
      let stylesChanged = false;

      Object.keys(activeStyles).forEach((style) => {
        const isCurrentlyActive = currentStyle.has(style);
        updatedActiveStyles[style] = isCurrentlyActive;

        if (isCurrentlyActive !== activeStyles[style]) {
          stylesChanged = true;
        }
      });

      if (stylesChanged) {
        setActiveStyles(updatedActiveStyles);
      }
    } else {
      Object.keys(activeStyles).forEach((style) => {
        const isActive = activeStyles[style];
        const isCurrentlyActive = currentStyle.has(style);

        if (isActive !== isCurrentlyActive) {
          setEditorState((prevState) =>
            RichUtils.toggleInlineStyle(prevState, style),
          );
        }
      });
    }
  };

  useEffect(() => {
    const applyCurrentStyles = () => {
      applyStyles(
        descriptionEnglish,
        setDescriptionEnglish,
        setActiveStylesDescriptionEng,
        activeStylesDescriptionEng,
      );
      applyStyles(
        descriptionLatin,
        setDescriptionLatin,
        setActiveStylesDescriptionLat,
        activeStylesDescriptionLat,
      );
      applyStyles(
        descriptionCyrillic,
        setDescriptionCyrillic,
        setActiveStylesDescriptionCir,
        activeStylesDescriptionCir,
      );

      applyStyles(
        highlightsEnglish,
        setHighlightsEnglish,
        setActiveStylesHighlightsEng,
        activeStylesHighlightsEng,
      );
      applyStyles(
        highlightsLatin,
        setHighlightsLatin,
        setActiveStylesHighlightsLat,
        activeStylesHighlightsLat,
      );
      applyStyles(
        highlightsCyrillic,
        setHighlightsCyrillic,
        setActiveStylesHighlightsCir,
        activeStylesHighlightsCir,
      );

      applyStyles(
        recommendedEnglish,
        setRecommendedEnglish,
        setActiveStylesRecommendedEng,
        activeStylesRecommendedEng,
      );
      applyStyles(
        recommendedLatin,
        setRecommendedLatin,
        setActiveStylesRecommendedLat,
        activeStylesRecommendedLat,
      );
      applyStyles(
        recommendedCyrillic,
        setRecommendedCyrillic,
        setActiveStylesRecommendedCir,
        activeStylesRecommendedCir,
      );
    };

    const tout = setTimeout(applyCurrentStyles, 0);
    return () => clearTimeout(tout);
  }, [
    descriptionEnglish,
    descriptionLatin,
    descriptionCyrillic,
    highlightsEnglish,
    highlightsLatin,
    highlightsCyrillic,
    recommendedEnglish,
    recommendedLatin,
    recommendedCyrillic,
    activeStylesDescriptionEng,
    activeStylesDescriptionLat,
    activeStylesDescriptionCir,
    activeStylesHighlightsEng,
    activeStylesHighlightsLat,
    activeStylesHighlightsCir,
    activeStylesRecommendedEng,
    activeStylesRecommendedLat,
    activeStylesRecommendedCir,
  ]);

  const buildWaypointFromLocation = (locationItem, currentWaypoints) => {
    return {
      name:
        locationItem.nameLat ||
        locationItem.nameEng ||
        locationItem.nameCir ||
        "",
      imageUrl:
        Array.isArray(locationItem.images) && locationItem.images.length > 0
          ? locationItem.images[0]
          : "",
      latitude: Number(locationItem.lat) || 0,
      longitude: Number(locationItem.lon) || 0,
      order: currentWaypoints.length,
    };
  };

  const handleAddWaypoint = (locationItem) => {
    const newWaypoint = buildWaypointFromLocation(locationItem, waypoints);
    const updatedWaypoints = normalizeWaypointOrder([
      ...waypoints,
      newWaypoint,
    ]);

    setWaypoints(updatedWaypoints);
    setWaypointSearch("");
  };

  const handleRemoveWaypoint = (indexToRemove) => {
    const updatedWaypoints = waypoints.filter(
      (_, index) => index !== indexToRemove,
    );

    setWaypoints(normalizeWaypointOrder(updatedWaypoints));
  };

  const handleMoveWaypointUp = (index) => {
    if (index === 0) return;

    const updatedWaypoints = [...waypoints];
    [updatedWaypoints[index - 1], updatedWaypoints[index]] = [
      updatedWaypoints[index],
      updatedWaypoints[index - 1],
    ];

    setWaypoints(normalizeWaypointOrder(updatedWaypoints));
  };

  const handleMoveWaypointDown = (index) => {
    if (index === waypoints.length - 1) return;

    const updatedWaypoints = [...waypoints];
    [updatedWaypoints[index], updatedWaypoints[index + 1]] = [
      updatedWaypoints[index + 1],
      updatedWaypoints[index],
    ];

    setWaypoints(normalizeWaypointOrder(updatedWaypoints));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    resetStatuses();

    if (!titleEnglish || !titleLatin || !titleCyrillic) {
      setErrorMessage("All title fields are required.");
      return;
    }

    if (!location.trim()) {
      setErrorMessage("Location is required.");
      return;
    }

    if (!selectedCategory) {
      setErrorMessage("Category is required.");
      return;
    }

    if (Number(priority) < 0 || Number(priority) > 5) {
      setErrorMessage("Priority must be between 0 and 5.");
      return;
    }

    if (!duration.trim()) {
      setErrorMessage("Duration is required.");
      return;
    }

    const normalizedWaypoints = normalizeWaypointOrder(waypoints);

    if (normalizedWaypoints.some((item) => !item.name)) {
      setErrorMessage("All waypoints must contain a valid name.");
      return;
    }

    const payload = {
      titleEng: titleEnglish.trim(),
      titleLat: titleLatin.trim(),
      titleCir: titleCyrillic.trim(),
      location: location.trim(),
      imageUrl: imageUrl?.trim?.() || "",
      category: selectedCategory?.nameEnglish || "",
      descriptionEng: convertToHTMLWrapper(descriptionEnglish),
      descriptionLat: convertToHTMLWrapper(descriptionLatin),
      descriptionCir: convertToHTMLWrapper(descriptionCyrillic),
      duration: duration.trim(),
      highlightsEng: convertToHTMLWrapper(highlightsEnglish),
      highlightsLat: convertToHTMLWrapper(highlightsLatin),
      highlightsCir: convertToHTMLWrapper(highlightsCyrillic),
      recommendedEng: convertToHTMLWrapper(recommendedEnglish),
      recommendedLat: convertToHTMLWrapper(recommendedLatin),
      recommendedCir: convertToHTMLWrapper(recommendedCyrillic),
      isFavorite,
      priority: Number(priority),
      waypoints: normalizedWaypoints,
    };

    setIsUploading(true);

    try {
      if (isEditMode) {
        await updateRoute({ id, payload });
      } else {
        await createRoute(payload);
      }

      setIsSuccess(true);

      setTimeout(() => {
        navigate("/predefined-routes");
      }, 1200);
    } catch (error) {
      console.error("Error saving predefined route:", error);
      setErrorMessage(
        error?.message || "An error occurred while saving the route.",
      );
      setIsFailed(true);
    } finally {
      setIsUploading(false);
    }
  };

  const handleLogOut = (e) => {
    e.preventDefault();
    sessionStorage.removeItem("user");
    dispatch(logout());
  };

  if (isLoadingCategories || (isEditMode && isLoadingRoute)) {
    return (
      <div className="invisibleWrapper">
        <p className="loadingText">Loading...</p>
      </div>
    );
  }

  return (
    <div className="admin-page predefined-route-page">
      <div className="titleContainer">
        {/* <p className="link">
          <Link to="/predefined-routes">Predefined Routes</Link>
        </p> */}
        <AdminNavigationLinks />

        <h1>{isEditMode ? "Izmena rute" : "Kreiranje rute"}</h1>
        <button className="logout" onClick={handleLogOut}>
          Izloguj se
        </button>
      </div>

      {errorMessage && <p className="error-msg">{errorMessage}</p>}

      <form className="form" onSubmit={handleSubmit}>
        <div className="multilingual">
          <label>Naslov</label>
          <input
            type="text"
            value={titleEnglish}
            onChange={(e) => setTitleEnglish(e.target.value)}
            placeholder="...na Engleskom"
            required
          />
          <input
            type="text"
            value={titleLatin}
            onChange={(e) => setTitleLatin(e.target.value)}
            placeholder="...na Latinici"
            required
          />
          <input
            type="text"
            value={titleCyrillic}
            onChange={(e) => setTitleCyrillic(e.target.value)}
            placeholder="...на Ћирилици"
            required
          />
        </div>

        <label>Lokacija</label>
        <input
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Unesi lokaciju rute"
          required
        />

        <label>Ubaci sliku</label>
        <div className="photoInputs-container">
          <div className="singleInput" onClick={handleImagePickerClick}>
            <input
              type="file"
              accept="image/*"
              ref={photoInputRef}
              onChange={handleImageUploaded}
            />
            <img
              className="thumbnail"
              src={imagePreview || photoImg}
              alt="route preview"
            />
            {(imagePreview || imageUrl) && (
              <span
                className="removeBtn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveImage();
                }}
              >
                x
              </span>
            )}
          </div>
        </div>

        <label>Izaberi kategoriju</label>
        <Select
          options={categories}
          value={selectedCategory}
          onChange={setSelectedCategory}
          getOptionLabel={(option) => option.nameLatin}
          getOptionValue={(option) => option.id}
          classNamePrefix="select"
          className="custom-select-panel"
          placeholder="Izaberi kategoriju"
        />

        <div className="route-meta-grid">
          <div>
            <label>Postavi prioritet (0-5)</label>
            <input
              type="number"
              min="0"
              max="5"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              required
            />
          </div>

          <div>
            <label>Trajanje rute</label>
            <input
              type="text"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="npr. 1.5h, 2h, 90min"
              required
            />
          </div>

          <div className="favorite-checkbox-row">
            <label>Omiljena ruta</label>
            <input
              className="checkboxForAccessibility"
              type="checkbox"
              checked={isFavorite}
              onChange={(e) => setIsFavorite(e.target.checked)}
            />
          </div>
        </div>

        <div className="multilingual">
          <label>Dodaj opis</label>

          <div
            className="editor-container"
            onClick={() =>
              handleEditorContainerClick(
                editorDescriptionEngRef,
                descriptionEnglish,
                setDescriptionEnglish,
              )
            }
          >
            <Editor
              ref={editorDescriptionEngRef}
              editorState={descriptionEnglish}
              onChange={setDescriptionEnglish}
              placeholder="...na Engleskom"
              handlePastedText={(text, html) =>
                handlePastedText(
                  text,
                  html,
                  descriptionEnglish,
                  setDescriptionEnglish,
                )
              }
            />
            <div className="editor-controls">
              <ImprovisedButton
                toggleFunction={() => toggleStyle("BOLD", "descriptionEng")}
                className={`style-button ${
                  activeStylesDescriptionEng.BOLD ? "active" : ""
                }`}
              >
                <img src={boldImg} alt="Bold" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() => toggleStyle("ITALIC", "descriptionEng")}
                className={`style-button ${
                  activeStylesDescriptionEng.ITALIC ? "active" : ""
                }`}
              >
                <img src={italicImg} alt="Italic" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() =>
                  toggleStyle("UNDERLINE", "descriptionEng")
                }
                className={`style-button ${
                  activeStylesDescriptionEng.UNDERLINE ? "active" : ""
                }`}
              >
                <img src={underlineImg} alt="Underline" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() =>
                  promptForLink(descriptionEnglish, setDescriptionEnglish)
                }
                className="style-button link"
              >
                <img src={linkImg} alt="Link" />
              </ImprovisedButton>
            </div>
          </div>

          <div
            className="editor-container"
            onClick={() =>
              handleEditorContainerClick(
                editorDescriptionLatRef,
                descriptionLatin,
                setDescriptionLatin,
              )
            }
          >
            <Editor
              ref={editorDescriptionLatRef}
              editorState={descriptionLatin}
              onChange={setDescriptionLatin}
              placeholder="...na Latinici"
              handlePastedText={(text, html) =>
                handlePastedText(
                  text,
                  html,
                  descriptionLatin,
                  setDescriptionLatin,
                )
              }
            />
            <div className="editor-controls">
              <ImprovisedButton
                toggleFunction={() => toggleStyle("BOLD", "descriptionLat")}
                className={`style-button ${
                  activeStylesDescriptionLat.BOLD ? "active" : ""
                }`}
              >
                <img src={boldImg} alt="Bold" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() => toggleStyle("ITALIC", "descriptionLat")}
                className={`style-button ${
                  activeStylesDescriptionLat.ITALIC ? "active" : ""
                }`}
              >
                <img src={italicImg} alt="Italic" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() =>
                  toggleStyle("UNDERLINE", "descriptionLat")
                }
                className={`style-button ${
                  activeStylesDescriptionLat.UNDERLINE ? "active" : ""
                }`}
              >
                <img src={underlineImg} alt="Underline" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() =>
                  promptForLink(descriptionLatin, setDescriptionLatin)
                }
                className="style-button link"
              >
                <img src={linkImg} alt="Link" />
              </ImprovisedButton>
            </div>
          </div>

          <div
            className="editor-container"
            onClick={() =>
              handleEditorContainerClick(
                editorDescriptionCirRef,
                descriptionCyrillic,
                setDescriptionCyrillic,
              )
            }
          >
            <Editor
              ref={editorDescriptionCirRef}
              editorState={descriptionCyrillic}
              onChange={setDescriptionCyrillic}
              placeholder="...на Ћирилици"
              handlePastedText={(text, html) =>
                handlePastedText(
                  text,
                  html,
                  descriptionCyrillic,
                  setDescriptionCyrillic,
                )
              }
            />
            <div className="editor-controls">
              <ImprovisedButton
                toggleFunction={() => toggleStyle("BOLD", "descriptionCir")}
                className={`style-button ${
                  activeStylesDescriptionCir.BOLD ? "active" : ""
                }`}
              >
                <img src={boldImg} alt="Bold" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() => toggleStyle("ITALIC", "descriptionCir")}
                className={`style-button ${
                  activeStylesDescriptionCir.ITALIC ? "active" : ""
                }`}
              >
                <img src={italicImg} alt="Italic" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() =>
                  toggleStyle("UNDERLINE", "descriptionCir")
                }
                className={`style-button ${
                  activeStylesDescriptionCir.UNDERLINE ? "active" : ""
                }`}
              >
                <img src={underlineImg} alt="Underline" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() =>
                  promptForLink(descriptionCyrillic, setDescriptionCyrillic)
                }
                className="style-button link"
              >
                <img src={linkImg} alt="Link" />
              </ImprovisedButton>
            </div>
          </div>
        </div>

        <div className="multilingual">
          <label>Izdvajamo</label>

          <div
            className="editor-container"
            onClick={() =>
              handleEditorContainerClick(
                editorHighlightsEngRef,
                highlightsEnglish,
                setHighlightsEnglish,
              )
            }
          >
            <Editor
              ref={editorHighlightsEngRef}
              editorState={highlightsEnglish}
              onChange={setHighlightsEnglish}
              placeholder="...na Engleskom"
              handlePastedText={(text, html) =>
                handlePastedText(
                  text,
                  html,
                  highlightsEnglish,
                  setHighlightsEnglish,
                )
              }
            />
            <div className="editor-controls">
              <ImprovisedButton
                toggleFunction={() => toggleStyle("BOLD", "highlightsEng")}
                className={`style-button ${
                  activeStylesHighlightsEng.BOLD ? "active" : ""
                }`}
              >
                <img src={boldImg} alt="Bold" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() => toggleStyle("ITALIC", "highlightsEng")}
                className={`style-button ${
                  activeStylesHighlightsEng.ITALIC ? "active" : ""
                }`}
              >
                <img src={italicImg} alt="Italic" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() => toggleStyle("UNDERLINE", "highlightsEng")}
                className={`style-button ${
                  activeStylesHighlightsEng.UNDERLINE ? "active" : ""
                }`}
              >
                <img src={underlineImg} alt="Underline" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() =>
                  promptForLink(highlightsEnglish, setHighlightsEnglish)
                }
                className="style-button link"
              >
                <img src={linkImg} alt="Link" />
              </ImprovisedButton>
            </div>
          </div>

          <div
            className="editor-container"
            onClick={() =>
              handleEditorContainerClick(
                editorHighlightsLatRef,
                highlightsLatin,
                setHighlightsLatin,
              )
            }
          >
            <Editor
              ref={editorHighlightsLatRef}
              editorState={highlightsLatin}
              onChange={setHighlightsLatin}
              placeholder="...na Latinici"
              handlePastedText={(text, html) =>
                handlePastedText(
                  text,
                  html,
                  highlightsLatin,
                  setHighlightsLatin,
                )
              }
            />
            <div className="editor-controls">
              <ImprovisedButton
                toggleFunction={() => toggleStyle("BOLD", "highlightsLat")}
                className={`style-button ${
                  activeStylesHighlightsLat.BOLD ? "active" : ""
                }`}
              >
                <img src={boldImg} alt="Bold" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() => toggleStyle("ITALIC", "highlightsLat")}
                className={`style-button ${
                  activeStylesHighlightsLat.ITALIC ? "active" : ""
                }`}
              >
                <img src={italicImg} alt="Italic" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() => toggleStyle("UNDERLINE", "highlightsLat")}
                className={`style-button ${
                  activeStylesHighlightsLat.UNDERLINE ? "active" : ""
                }`}
              >
                <img src={underlineImg} alt="Underline" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() =>
                  promptForLink(highlightsLatin, setHighlightsLatin)
                }
                className="style-button link"
              >
                <img src={linkImg} alt="Link" />
              </ImprovisedButton>
            </div>
          </div>

          <div
            className="editor-container"
            onClick={() =>
              handleEditorContainerClick(
                editorHighlightsCirRef,
                highlightsCyrillic,
                setHighlightsCyrillic,
              )
            }
          >
            <Editor
              ref={editorHighlightsCirRef}
              editorState={highlightsCyrillic}
              onChange={setHighlightsCyrillic}
              placeholder="...на Ћирилици"
              handlePastedText={(text, html) =>
                handlePastedText(
                  text,
                  html,
                  highlightsCyrillic,
                  setHighlightsCyrillic,
                )
              }
            />
            <div className="editor-controls">
              <ImprovisedButton
                toggleFunction={() => toggleStyle("BOLD", "highlightsCir")}
                className={`style-button ${
                  activeStylesHighlightsCir.BOLD ? "active" : ""
                }`}
              >
                <img src={boldImg} alt="Bold" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() => toggleStyle("ITALIC", "highlightsCir")}
                className={`style-button ${
                  activeStylesHighlightsCir.ITALIC ? "active" : ""
                }`}
              >
                <img src={italicImg} alt="Italic" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() => toggleStyle("UNDERLINE", "highlightsCir")}
                className={`style-button ${
                  activeStylesHighlightsCir.UNDERLINE ? "active" : ""
                }`}
              >
                <img src={underlineImg} alt="Underline" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() =>
                  promptForLink(highlightsCyrillic, setHighlightsCyrillic)
                }
                className="style-button link"
              >
                <img src={linkImg} alt="Link" />
              </ImprovisedButton>
            </div>
          </div>
        </div>

        <div className="multilingual">
          <label>Preporučujemo</label>

          <div
            className="editor-container"
            onClick={() =>
              handleEditorContainerClick(
                editorRecommendedEngRef,
                recommendedEnglish,
                setRecommendedEnglish,
              )
            }
          >
            <Editor
              ref={editorRecommendedEngRef}
              editorState={recommendedEnglish}
              onChange={setRecommendedEnglish}
              placeholder="...na Engleskom"
              handlePastedText={(text, html) =>
                handlePastedText(
                  text,
                  html,
                  recommendedEnglish,
                  setRecommendedEnglish,
                )
              }
            />
            <div className="editor-controls">
              <ImprovisedButton
                toggleFunction={() => toggleStyle("BOLD", "recommendedEng")}
                className={`style-button ${
                  activeStylesRecommendedEng.BOLD ? "active" : ""
                }`}
              >
                <img src={boldImg} alt="Bold" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() => toggleStyle("ITALIC", "recommendedEng")}
                className={`style-button ${
                  activeStylesRecommendedEng.ITALIC ? "active" : ""
                }`}
              >
                <img src={italicImg} alt="Italic" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() =>
                  toggleStyle("UNDERLINE", "recommendedEng")
                }
                className={`style-button ${
                  activeStylesRecommendedEng.UNDERLINE ? "active" : ""
                }`}
              >
                <img src={underlineImg} alt="Underline" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() =>
                  promptForLink(recommendedEnglish, setRecommendedEnglish)
                }
                className="style-button link"
              >
                <img src={linkImg} alt="Link" />
              </ImprovisedButton>
            </div>
          </div>

          <div
            className="editor-container"
            onClick={() =>
              handleEditorContainerClick(
                editorRecommendedLatRef,
                recommendedLatin,
                setRecommendedLatin,
              )
            }
          >
            <Editor
              ref={editorRecommendedLatRef}
              editorState={recommendedLatin}
              onChange={setRecommendedLatin}
              placeholder="...na Latinici"
              handlePastedText={(text, html) =>
                handlePastedText(
                  text,
                  html,
                  recommendedLatin,
                  setRecommendedLatin,
                )
              }
            />
            <div className="editor-controls">
              <ImprovisedButton
                toggleFunction={() => toggleStyle("BOLD", "recommendedLat")}
                className={`style-button ${
                  activeStylesRecommendedLat.BOLD ? "active" : ""
                }`}
              >
                <img src={boldImg} alt="Bold" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() => toggleStyle("ITALIC", "recommendedLat")}
                className={`style-button ${
                  activeStylesRecommendedLat.ITALIC ? "active" : ""
                }`}
              >
                <img src={italicImg} alt="Italic" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() =>
                  toggleStyle("UNDERLINE", "recommendedLat")
                }
                className={`style-button ${
                  activeStylesRecommendedLat.UNDERLINE ? "active" : ""
                }`}
              >
                <img src={underlineImg} alt="Underline" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() =>
                  promptForLink(recommendedLatin, setRecommendedLatin)
                }
                className="style-button link"
              >
                <img src={linkImg} alt="Link" />
              </ImprovisedButton>
            </div>
          </div>

          <div
            className="editor-container"
            onClick={() =>
              handleEditorContainerClick(
                editorRecommendedCirRef,
                recommendedCyrillic,
                setRecommendedCyrillic,
              )
            }
          >
            <Editor
              ref={editorRecommendedCirRef}
              editorState={recommendedCyrillic}
              onChange={setRecommendedCyrillic}
              placeholder="...на Ћирилици"
              handlePastedText={(text, html) =>
                handlePastedText(
                  text,
                  html,
                  recommendedCyrillic,
                  setRecommendedCyrillic,
                )
              }
            />
            <div className="editor-controls">
              <ImprovisedButton
                toggleFunction={() => toggleStyle("BOLD", "recommendedCir")}
                className={`style-button ${
                  activeStylesRecommendedCir.BOLD ? "active" : ""
                }`}
              >
                <img src={boldImg} alt="Bold" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() => toggleStyle("ITALIC", "recommendedCir")}
                className={`style-button ${
                  activeStylesRecommendedCir.ITALIC ? "active" : ""
                }`}
              >
                <img src={italicImg} alt="Italic" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() =>
                  toggleStyle("UNDERLINE", "recommendedCir")
                }
                className={`style-button ${
                  activeStylesRecommendedCir.UNDERLINE ? "active" : ""
                }`}
              >
                <img src={underlineImg} alt="Underline" />
              </ImprovisedButton>
              <ImprovisedButton
                toggleFunction={() =>
                  promptForLink(recommendedCyrillic, setRecommendedCyrillic)
                }
                className="style-button link"
              >
                <img src={linkImg} alt="Link" />
              </ImprovisedButton>
            </div>
          </div>
        </div>

        <div className="multilingual waypoint-section">
          <label>Stanice na putu</label>

          <input
            type="text"
            value={waypointSearch}
            onChange={(e) => setWaypointSearch(e.target.value)}
            placeholder="Unesi najmanje 3 karaktera za pretragu lokaliteta"
          />

          {waypointSearch.trim().length > 0 &&
            waypointSearch.trim().length < 3 && (
              <p className="waypoint-helper-text">
                Unesi najmanje 3 karaktera za pretragu.
              </p>
            )}

          {isLoadingWaypointSuggestions &&
            waypointSearch.trim().length >= 3 && (
              <p className="waypoint-helper-text">Pretražujemo lokacije...</p>
            )}

          {waypointSearch.trim().length >= 3 &&
            locationSuggestions?.locations?.length > 0 && (
              <div className="waypoint-suggestions">
                {locationSuggestions.locations.map((locationItem) => (
                  <div
                    key={locationItem.id}
                    className="waypoint-suggestion-item"
                    onClick={() => handleAddWaypoint(locationItem)}
                  >
                    <div className="waypoint-suggestion-main">
                      <strong>
                        {locationItem.nameLat ||
                          locationItem.nameEng ||
                          locationItem.nameCir}
                      </strong>
                      <span>
                        {locationItem.lat}, {locationItem.lon}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

          {waypointSearch.trim().length >= 3 &&
            !isLoadingWaypointSuggestions &&
            locationSuggestions?.locations?.length === 0 && (
              <p className="waypoint-helper-text">Nema rezultata.</p>
            )}

          <div className="waypoint-list">
            {waypoints.length === 0 && (
              <p className="waypoint-helper-text">
                Nema dodatih stanica na putu.
              </p>
            )}

            {waypoints.map((waypoint, index) => (
              <div key={`${waypoint.name}-${index}`} className="waypoint-card">
                <div className="waypoint-card-info">
                  <p>
                    <strong>
                      {index + 1}. {waypoint.name}
                    </strong>
                  </p>
                  <p>Latitude: {waypoint.latitude}</p>
                  <p>Longitude: {waypoint.longitude}</p>
                  <p>Order: {waypoint.order}</p>
                  {waypoint.imageUrl && (
                    <p className="waypoint-image-url">{waypoint.imageUrl}</p>
                  )}
                </div>

                <div className="waypoint-card-actions">
                  <button
                    type="button"
                    onClick={() => handleMoveWaypointUp(index)}
                    disabled={index === 0}
                  >
                    Gore
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMoveWaypointDown(index)}
                    disabled={index === waypoints.length - 1}
                  >
                    Dole
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemoveWaypoint(index)}
                  >
                    Ukloni
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button className="btn-submit" type="submit" disabled={isUploading}>
          {isEditMode ? "Sačuvaj izmene rute" : "Dodaj rutu"}
        </button>

        {isUploading && (
          <div className="spinner-container">
            <div className="spinner"></div>
          </div>
        )}

        {isSuccess && (
          <div className="spinner-container">
            <img className="successgif" src={success} alt="success" />
          </div>
        )}

        {isFailed && (
          <div className="spinner-container">
            <img className="failedgif" src={failed} alt="failed" />
          </div>
        )}
      </form>
    </div>
  );
};

export default PredefinedRouteForm;
